import {
    useEffect, useState
} from 'react';
import {
    Button
} from 'shared/components/ui/button';

import {
    ScrollArea
} from 'shared/components/ui/scroll-area';
import {
    Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from 'shared/components/ui/table';
import {
    Card, CardContent, CardHeader, CardTitle
} from 'shared/components/ui/card';
import {
    useParams, useNavigate
} from 'react-router-dom';
import {
    opcodeAssistantService
} from 'shared/api';
import {
    jsonOpcodeService
} from 'shared/api/services/BEX/jsonOpcode.service';
import {
    Check, ChevronsUpDown
} from 'lucide-react';
import {
    cn
} from 'shared/lib/utils';
import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandSeparator,
} from 'shared/components/ui/command';
import {
    Popover, PopoverContent, PopoverTrigger
} from 'shared/components/ui/popover';

import {
    ComboBoxItem, OpCodeAssistant
} from '../types/builderAssistant.types';

type ResolvedOpcode = OpCodeAssistant & {
    description?: string;
    id?: string;
    // Internal-only: pre-computed relative path for click-through. Not shown
    // to the user. Post-Addendum the UI treats every opcode as just "an
    // opcode"; the classic/json distinction is a backend storage detail.
    _routeTo?: string;
};

export default function OpcodeInstructionDialog() {
    const [savedOpcodes, setSavedOpcodes] = useState<ResolvedOpcode[]>([]);

    const {
        oragID, subOragID, assistantId
    } = useParams<{ oragID: string; subOragID: string; assistantId: string }>();

    const getOpCodeAssistant = async () => {
        try {
            // A-0008.03: V1 ("Classic") opcodes removed. Single source: V2+ list.
            const resJson = await jsonOpcodeService.listJSONOpcodes({
                organizationId: oragID!,
                subOrganizationId: subOragID!,
            });

            const rawJsonList: any[] = Array.isArray(resJson?.data) ? resJson.data : [];

            const opcodeMap = new Map<string, { id: string; name: string; description: string }>();
            rawJsonList.forEach((op: any) => {
                const tokenId = op._id || op.opcode_id || op.id;
                const friendly = op.title || op.name || tokenId;
                const value = {
                    id: tokenId,
                    name: friendly,
                    description: friendly,
                };
                if (tokenId) opcodeMap.set(tokenId, value);
                if (op.name) opcodeMap.set(op.name, value);
                if (op.title) opcodeMap.set(op.title, value);
            });

            // 2. Fetch Assistant details
            const res = await opcodeAssistantService.getOpcodeAssistant(oragID, subOragID, assistantId);

            // 3. Map attached OpCodes. Post-Addendum, `opcodes` is always an
            // array of rich entries: {opcode_id, purpose, description, input_mapping}.
            const rawOpcodes: any[] = res.data.opcodes || [];
            const opcodeIds = rawOpcodes
                .map((e: any) => (typeof e === 'string' ? e : e?.opcode_id))
                .filter(Boolean);

            const resolvedOpcodes: ResolvedOpcode[] = opcodeIds.map((item: string) => {
                // Look up in both backend stores; the UI doesn't care which
                // one owns the opcode — the user just sees "an opcode".
                let friendlyName = item;
                let targetId = item;
                let routeTo = '';

                if (opcodeMap.has(item)) {
                    const data = opcodeMap.get(item)!;
                    friendlyName = data.description;
                    targetId = data.id;
                    routeTo = `../../op-codes/v2/${data.id}`;
                }

                return {
                    name: item,
                    description: friendlyName,
                    default: res.data.default_opcode == item ? 'true' : 'false',
                    id: targetId,
                    _routeTo: routeTo,
                };
            });

            setSavedOpcodes(resolvedOpcodes);

        } catch {
            /* empty */
        }
    };

    useEffect(() => {
        getOpCodeAssistant();
    }, []);

    return (
        <div className="flex w-full mt-4 flex-col">
            <Card>
                <CardHeader>
                    <CardTitle className="text-lg font-semibold">OpCodes Link to This Assistant</CardTitle>
                </CardHeader>
                <CardContent>
                    <div>
                        <h3 className="mb-2 font-semibold">OpCodes:</h3>
                        <DisplayOpCodeAssistant opcodes={savedOpcodes} />
                    </div>
                </CardContent>

                <CardContent></CardContent>
            </Card>
        </div>
    );
}

interface DisplayOpCodeAssistantProps {
  opcodes: ResolvedOpcode[];
}

export const DisplayOpCodeAssistant = ({
    opcodes
}: DisplayOpCodeAssistantProps) => {

    const navigate = useNavigate();

    return (
        <Table>
            <TableHeader>
                <TableRow>
                    <TableHead>Opcode</TableHead>
                    <TableHead>Default</TableHead>
                </TableRow>
            </TableHeader>
            <TableBody>
                {opcodes.map((opcode, i) => (
                    <TableRow
                        key={i}
                        className="cursor-pointer hover:bg-muted/50"
                        onClick={() => { if (opcode._routeTo) navigate(opcode._routeTo); }}
                    >
                        <TableCell>
                            <div>
                                <div className="font-medium text-sm">{opcode.description || opcode.name}</div>
                                {(opcode.description && opcode.description !== opcode.id) && (
                                    <div className="text-xs text-muted-foreground font-mono">{opcode.id || opcode.name}</div>
                                )}
                            </div>
                        </TableCell>
                        <TableCell>{opcode.default === 'true' && 'true'}</TableCell>
                    </TableRow>
                ))}
            </TableBody>
        </Table>
    );
};

interface AssistantComboboxProps {
  className?: string;
  items: ComboBoxItem[];
  selectedValue: ComboBoxItem | null;
  setter: (item: ComboBoxItem) => void;
  disabled?: boolean;
  defaultDisplayed?: string;
}

export function AssistantCombobox({
    className,
    items,
    selectedValue,
    setter,
    disabled = false,
    defaultDisplayed = 'Select Item',
}: AssistantComboboxProps) {
    const [open, setOpen] = useState(false);
    const [value, setValue] = useState<ComboBoxItem | null>(selectedValue);

    useEffect(() => {
        setValue(selectedValue);
    }, [selectedValue]);

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger disabled={disabled} asChild>
                <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={open}
                    className="w-full justify-between text-accent-foreground overflow-hidden"
                >
                    <span className="truncate" title={value ? value?.value : defaultDisplayed ? defaultDisplayed : 'Select Item'}>
                        {value ? value?.value : defaultDisplayed ? defaultDisplayed : 'Select Item'}
                    </span>
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
            </PopoverTrigger>
            <PopoverContent className={cn('w-full p-0 z-[9999]', className)}>
                <Command>
                    <CommandInput placeholder="Search..." />
                    <CommandEmpty>No item found.</CommandEmpty>
                    <CommandGroup>
                        <ScrollArea className="max-h-60">
                            {items?.map((item) => (
                                <CommandItem
                                    key={item?.label}
                                    value={item?.value}
                                    onSelect={() => {
                                        setValue(item);
                                        setter && setter(item);
                                        setOpen(false);
                                    }}
                                >
                                    <Check className={cn('mr-2 h-4 w-4', value?.value === item?.value ? 'opacity-100' : 'opacity-0')} />
                                    {item?.value}
                                </CommandItem>
                            ))}
                        </ScrollArea>
                    </CommandGroup>
                    <CommandSeparator />
                </Command>
            </PopoverContent>
        </Popover>
    );
}
